import type { Translation } from '../types.ts';
import type en from '../en/forms.ts';

export default {
    shared: {
        fromEmail: 'E-mailadres afzender (optioneel)',
        fromEmailHint: 'Overschrijft het standaard afzenderadres van de website voor inzendingen van dit formulier.',
        fromName: 'Naam afzender (optioneel)',
        fallsBack: 'Valt terug op de standaard van de website',
        recipientPlaceholder: "info{'@'}voorbeeld.nl",
        successUrlPlaceholder: '/bedankt',
    },
    card: {
        title: 'Formulieren',
        intro: 'Formulieren worden automatisch gevonden in de templates van het actieve thema ({code}). Stel per formulier in waar de inzendingen naartoe gaan.',
        empty: 'Nog geen formulieren gevonden in het actieve thema.',
        removeConfirm: 'Configuratie verwijderen?',
        removeTitle: 'Configuratie verwijderen (het formulier blijft in het thema; inzendingen geven dan een 404)',
        recipient: 'E-mailadres ontvanger',
        subject: 'Onderwerp e-mail',
        subjectPlaceholder: 'Contactformulier',
        subjectHint: "Onderwerpregel van de notificatiemail. Met {'{'}field:naam{'}'} komt erbij wat er in dat veld is ingevuld (de naam van het veld in het formulier), bijv. Offerte aanvraag {'{'}field:service{'}'}.",
        successUrl: 'Doorsturen na verzenden',
        successUrlHint: 'Laat leeg om terug te gaan naar de pagina waar het formulier op staat.',
        configure: 'Instellen',
        status: {
            active: 'actief',
            detected: 'gevonden, niet ingesteld',
            orphan: 'wees (niet in thema)',
            unknown: 'onbekend',
        },
    },
    settings: {
        title: 'Formulierinstellingen',
        delivery: 'Bezorging',
        recipients: 'Ontvanger(s)',
        recipientsHint: 'E-mailadres dat de inzendingen ontvangt. Scheid meerdere adressen met komma\'s.',
        subject: 'Onderwerp',
        subjectHint: "Onderwerpregel van de notificatiemail. Met {'{'}field:naam{'}'} komt erbij wat er in dat veld is ingevuld (de naam van het veld in het formulier), bijv. Offerte aanvraag {'{'}field:service{'}'}.",
        subjectPlaceholder: 'Inzending contactformulier',
        successUrl: 'URL na verzenden',
        successUrlHint: 'Pad om naartoe te sturen na een geslaagde inzending. Laat leeg om de bevestiging in het formulier zelf te tonen.',
        retention: 'Geüploade bestanden bewaren (dagen)',
        retentionHint: 'Geldt alleen voor formulieren waarmee bestanden geüpload kunnen worden. Daarna worden de bestanden verwijderd; de inzending blijft bewaard en toont de bijlage als verlopen.',
        retentionPlaceholder: '{days} (standaard)',
        labelsAndText: 'Labels en tekst',
        defaultLocale: 'standaard',
        fieldLabels: 'Veldlabels',
        placeholders: 'Placeholders',
        button: 'Knop',
        submitButton: 'Verzendknop',
    },
    field: {
        title: 'Bewerken · {name}',
        titleFallback: 'Veld bewerken',
        pickImage: 'Kies of upload een afbeelding',
        pickVideo: 'Kies of upload een video',
        pickAny: 'Kies of upload een afbeelding of video',
        noImage: 'Geen afbeelding',
        noVideo: 'Geen video',
        source: 'Bron',
        replace: 'Vervangen…',
        choose: 'Kiezen…',
        altText: 'Alt-tekst',
        alt: 'Alt',
        caption: 'Bijschrift',
        width: 'Breedte',
        alignment: 'Uitlijning',
        cornerRadius: 'Hoekafronding',
        fit: 'Passend maken',
        size: {
            small: 'Klein',
            medium: 'Middel',
            large: 'Groot',
            full: 'Volledig',
        },
        align: {
            left: 'Links',
            center: 'Midden',
            right: 'Rechts',
        },
        radius: {
            circle: 'Cirkel',
            same: 'Gelijk',
        },
        fitOption: {
            auto: 'Automatisch',
            cover: 'Vullend',
        },
        translate: {
            editing: 'Je bewerkt {locale}.',
            editingFrom: 'Je bewerkt {locale}, vertaald uit {source}.',
            button: 'Dit veld vertalen',
            buttonTitle: 'Vul het vak hieronder met een {locale}-vertaling. Er wordt niets opgeslagen tot je op Toepassen en daarna op Opslaan drukt.',
            done: 'Vertaald. Controleer het en druk dan op Toepassen',
            skipped: 'De server heeft dit veld niet aangeraakt: er is niets veranderd',
            failed: 'De vertaler kon dit veld niet teruggeven',
        },
        html: {
            content: 'Inhoud',
        },
        text: {
            label: 'Tekst',
            hint: 'Het thema bepaalt de omringende tag (kop, alinea, enz.). Alleen opmaak binnen de regel.',
        },
        image: {
            altHint: 'Beschrijf de afbeelding voor schermlezers en SEO.',
            focalPoint: 'Focuspunt',
            resetFocal: 'Terug naar het midden',
            focalHelp: 'Klik of sleep om te kiezen wat in beeld blijft als de afbeelding wordt bijgesneden.',
        },
        gallery: {
            columns: 'Kolommen',
            columnsAuto: 'Automatisch',
            items: 'Items',
            add: 'Afbeelding of video toevoegen',
            empty: 'Nog geen afbeeldingen. Klik op “Afbeelding of video toevoegen” om er een uit de bibliotheek te kiezen.',
        },
        slideshow: {
            translatingNotice: 'Je bewerkt alleen de {locale}-tekst. De afbeeldingen, knopdoelen en timing zijn voor elke taal gelijk. Wijzig die op het tabblad {source}, dan volgen ze hier automatisch.',
            height: 'Hoogte',
            heightSmall: 'Klein (40vh)',
            heightMedium: 'Middel (60vh)',
            heightLarge: 'Groot (80vh)',
            heightFull: 'Volledig (100vh)',
            heightFromTheme: 'Dit thema bepaalt de hoogte van de slider zelf, dus deze instelling heeft op deze site geen effect.',
            fit: 'Foto passen',
            fitCover: 'Slider vullen',
            fitContain: 'Hele foto',
            fitCoverHint: 'Vult de slider; wat niet past wordt afgesneden.',
            fitContainHint: 'Toont elke foto helemaal; de ruimte eromheen krijgt een zachte kopie van de foto.',
            interval: 'Interval (ms)',
            intervalHint: 'Tijd tussen de slides, tussen 1000 en 60000.',
            pauseOnHover: 'Pauzeren bij hover',
            showDots: 'Bolletjes tonen',
            showArrows: 'Pijlen tonen',
            slides: 'Slides',
            add: 'Slide toevoegen',
            heading: 'Kop',
            altHint: 'Beschrijft de afbeelding (verplicht voor toegankelijkheid).',
            cta: 'Call-to-action',
            ctaNone: 'Geen knop',
            ctaUrl: 'Externe URL',
            ctaPage: 'Pagina op deze site',
            ctaCategory: 'Webshopcategorie',
            ctaProduct: 'Product',
            buttonLabel: 'Knoptekst',
            buttonLabelHint: 'Getoond in {locale}.',
            urlPlaceholder: 'https://voorbeeld.nl of /contact',
            urlHint: 'Absolute http(s)-URL of pad vanaf de root.',
            page: 'Pagina',
            pageHint: 'Kies een pagina op deze website. Hernoemde pagina\'s moet je hier opnieuw kiezen.',
            category: 'Categorie',
            categoryHint: 'Webshopcategorieën op het hoogste niveau. Elke taal linkt naar het eigen vertaalde adres.',
            product: 'Product',
            productHint: 'Typ om in de catalogus te zoeken. Elke taal linkt naar het eigen vertaalde adres.',
            empty: 'Nog geen slides. Klik op “Slide toevoegen” om er een uit de bibliotheek te kiezen.',
        },
        video: {
            autoplay: 'Automatisch afspelen',
            autoplayHint: 'Altijd zonder geluid: browsers spelen een video met geluid niet automatisch af. Het beste voor een korte achtergrondloop.',
            loop: 'Herhalen',
            controls: 'Bediening tonen',
            controlsHint: 'Zet uit voor een achtergrondvideo die de bezoeker niet moet kunnen pauzeren.',
            mobile: 'Op telefoons',
            mobileHint: 'Een stilstaand beeld bespaart een grote download op het kleinste scherm en de slechtste verbinding.',
            mobileVideo: 'De video afspelen',
            mobilePoster: 'Een stilstaand beeld tonen',
            altHint: 'Beschrijft de video voor schermlezers en dient als label van het stilstaande beeld.',
            hideCorners: 'Verbergen',
            setCorners: 'Hoeken afzonderlijk instellen',
            cornerTopLeft: 'Linksboven',
            cornerTopRight: 'Rechtsboven',
            cornerBottomLeft: 'Linksonder',
            cornerBottomRight: 'Rechtsonder',
            expandable: 'Bezoekers laten vergroten',
            expandableHint: 'Wordt groter op de pagina, niet schermvullend in de browser. Klikken ernaast zet hem terug.',
        },
        compare: {
            before: 'Voor',
            after: 'Na',
            missingBoth: 'Kies beide afbeeldingen. Een vergelijking heeft er twee nodig.',
            missingAfter: 'Kies ook de "na"-afbeelding.',
            missingBefore: 'Kies ook de "voor"-afbeelding.',
            imageLabel: 'Label op de afbeelding',
            imageLabelHint: 'Optioneel.',
            startingPosition: 'Beginpositie',
            startingHelp: '{n}% van de "na"-afbeelding is zichtbaar voordat iemand sleept.',
            direction: 'Schuifrichting',
            horizontal: 'Links en rechts',
            vertical: 'Boven en onder',
        },
        embed: {
            code: 'HTML-/iframe-code',
            hint: 'Wordt bij opslaan opgeschoond: alleen veilige iframe- en scripttags van goedgekeurde hosts blijven over.',
        },
        divider: {
            none: 'Scheidingslijnen hebben geen instellingen: ze dienen alleen als visuele scheiding.',
        },
        unknown: 'Nog geen editor beschikbaar voor velden van het type {type}.',
    },
} satisfies Translation<typeof en>;
